/**
 * Terminal Console - Enterprise Dashboard
 * Web terminal sicuro con gestione sessioni e monitoraggio processi
 */

import { useState } from 'react'
import { useAuthContext } from '@/contexts/AuthContext'
import { AdminLayout } from '../../layout/AdminLayout'
import { EnterprisePageHeader } from '../../components/layout/EnterprisePageHeader'
import { EnterpriseEmptyState } from '../../components/layout/EnterpriseEmptyState'
import { EnterpriseSectionSkeleton } from '../../components/layout/EnterpriseSectionSkeleton'
import { EnterpriseStickyActionBar } from '../../components/layout/EnterpriseStickyActionBar'
import { useTerminalConsole } from '@/hooks/useTerminalConsole'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { ScrollArea } from '@/shared/components/ui/scroll-area'
import { Input } from '@/shared/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs'
import { toast } from '@/shared/hooks/use-toast'
import { 
  Terminal, 
  Play, 
  Square,
  Plus,
  X,
  Monitor,
  Cpu,
  HardDrive,
  Activity,
  Clock,
  Zap,
  AlertCircle,
  CheckCircle,
} from 'lucide-react'

export default function TerminalConsole() {
  const { user, logout } = useAuthContext()
  const {
    sessions,
    activeSession,
    commandHistory,
    systemInfo,
    processes,
    currentOutput,
    isLoading,
    error,
    setActiveSession,
    executeCommand,
    createSession,
    closeSession,
    refresh
  } = useTerminalConsole()

  const [currentCommand, setCurrentCommand] = useState('')
  const [commandExecuting, setCommandExecuting] = useState(false)
  const [newSessionName, setNewSessionName] = useState('')

  const handleLogout = () => {
    void logout()
  }

  const handleExecuteCommand = async () => {
    if (!currentCommand.trim() || commandExecuting) return
    
    setCommandExecuting(true)
    try {
      const result = await executeCommand(currentCommand.trim())
      if (!result.success) {
        toast({
          title: 'Comando fallito',
          description: result.output || 'Errore sconosciuto',
          variant: 'destructive',
        })
      }
      setCurrentCommand('')
    } finally {
      setCommandExecuting(false)
    }
  }

  const handleCreateSession = async () => {
    if (!newSessionName.trim()) return
    
    const result = await createSession(newSessionName.trim())
    if (result.success) {
      setNewSessionName('')
      toast({ title: 'Sessione creata', description: `Sessione "${newSessionName.trim()}" pronta.` })
    } else {
      toast({
        title: 'Creazione sessione fallita',
        description: result.error || 'Errore sconosciuto',
        variant: 'destructive',
      })
    }
  }

  const getSessionBadge = (status: string) => {
    switch (status) {
      case 'active': return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Active</Badge>
      case 'inactive': return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">Inactive</Badge>
      case 'closed': return <Badge variant="destructive">Closed</Badge>
      default: return <Badge variant="outline">Unknown</Badge>
    }
  }

  const getProcessStatusBadge = (status: string) => {
    switch (status) {
      case 'running': return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Running</Badge>
      case 'sleeping': return <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20">Sleeping</Badge>
      case 'stopped': return <Badge variant="destructive">Stopped</Badge>
      default: return <Badge variant="outline">{status}</Badge>
    }
  }
  return (
    <AdminLayout user={user} onLogout={handleLogout}>
      <div className="space-y-6 sm:space-y-8">
        <EnterprisePageHeader
          badge="Terminal"
          title="Terminal Console"
          subtitle="Web terminal sicuro con gestione sessioni e monitoraggio processi"
          icon={<Terminal className="h-6 w-6" />}
          isLoading={isLoading}
          onRefresh={refresh}
        />

        {/* System Info Overview */}
        {systemInfo && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 sm:gap-6">
            <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Monitor className="h-4 w-4 text-blue-500" />
                    System
                  </div>
                  <Badge variant="outline" className="text-xs">Live</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-foreground">{systemInfo.hostname}</div>
                <div className="text-sm text-muted-foreground mt-1">{systemInfo.os}</div>
                <div className="text-xs text-muted-foreground">{systemInfo.kernel}</div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-green-500" />
                    Uptime
                  </div>
                  <Badge variant="outline" className="text-xs">System</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-foreground">{systemInfo.uptime}</div>
                <div className="text-sm text-muted-foreground mt-1">
                  Load: {systemInfo.load_average.join(', ')}
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Cpu className="h-4 w-4 text-orange-500" />
                    CPU & Memory
                  </div>
                  <Badge variant="outline" className="text-xs">Hardware</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-foreground">{systemInfo.cpu_cores} cores</div>
                <div className="text-sm text-muted-foreground mt-1">{systemInfo.memory_total} RAM</div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <HardDrive className="h-4 w-4 text-purple-500" />
                    Storage
                  </div>
                  <Badge variant="outline" className="text-xs">Disk</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm font-bold text-foreground">{systemInfo.disk_usage}</div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
          {/* Terminal Interface */}
          <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Terminal className="h-5 w-5 text-primary" />
                  Terminal Console
                </div>
                {activeSession && getSessionBadge(activeSession.status)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <EnterpriseStickyActionBar position="top">
                <Input
                  placeholder="New session name..."
                  value={newSessionName}
                  onChange={(e) => setNewSessionName(e.target.value)}
                  className="min-w-[220px] flex-1"
                />
                <Button onClick={handleCreateSession} size="sm" disabled={!newSessionName.trim()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Nuova Sessione
                </Button>
              </EnterpriseStickyActionBar>
              <Tabs defaultValue="terminal" className="w-full">
                <TabsList className="grid h-auto w-full grid-cols-2 gap-1 sm:grid-cols-3">
                  <TabsTrigger value="terminal" className="text-xs sm:text-sm">Terminal</TabsTrigger>
                  <TabsTrigger value="sessions" className="text-xs sm:text-sm">Sessions</TabsTrigger>
                  <TabsTrigger value="history" className="text-xs sm:text-sm">History</TabsTrigger>
                </TabsList>

                <TabsContent value="terminal" className="space-y-4">
                  {/* Terminal Output */}
                  <div className="bg-black text-green-400 p-4 rounded-lg font-mono text-sm min-h-64">
                    <ScrollArea className="h-64">
                      {activeSession ? (
                        <div className="space-y-1">
                          <div className="text-green-300">
                            ummr@{systemInfo?.hostname || 'server'}:~$ Welcome to UMMR Terminal
                          </div>
                          <div className="text-green-300">
                            Session: {activeSession.name} (ID: {activeSession.id})
                          </div>
                          <div className="text-green-300">
                            Type 'help' for available commands
                          </div>
                          <div className="border-t border-green-800 my-2"></div>
                          {currentOutput && (
                            <div className="whitespace-pre-wrap text-gray-300">
                              {currentOutput}
                            </div>
                          )}
                          <div className="flex items-center">
                            <span className="text-green-300">ummr@{systemInfo?.hostname || 'server'}:~$ </span>
                            <span className="ml-1 bg-green-400 w-2 h-4 animate-pulse"></span>
                          </div>
                        </div>
                      ) : (
                        <EnterpriseEmptyState
                          icon={<Terminal className="h-8 w-8 opacity-50" />}
                          title="Nessuna sessione attiva"
                          description="Crea o seleziona una sessione per usare il terminale."
                          className="border-green-900/30 bg-black/20 text-gray-300"
                        />
                      )}
                    </ScrollArea>
                  </div>

                  {/* Command Input */}
                  <EnterpriseStickyActionBar>
                    <Input
                      placeholder="Enter command..."
                      value={currentCommand}
                      onChange={(e) => setCurrentCommand(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleExecuteCommand()}
                      disabled={!activeSession || commandExecuting}
                      className="min-w-[220px] flex-1 font-mono"
                    />
                    <Button 
                      onClick={handleExecuteCommand}
                      disabled={!activeSession || !currentCommand.trim() || commandExecuting}
                    >
                      {commandExecuting ? (
                        <Square className="h-4 w-4" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </Button>
                  </EnterpriseStickyActionBar>
                </TabsContent>

                <TabsContent value="sessions" className="space-y-4">
                  <ScrollArea className="h-64">
                    {isLoading ? (
                      <EnterpriseSectionSkeleton rows={4} showHeader={false} rowClassName="h-20" />
                    ) : sessions.length > 0 ? (
                      <div className="space-y-2">
                        {sessions.map((session) => (
                          <div
                            key={session.id}
                            className={`p-3 border rounded-lg cursor-pointer transition-all duration-200 ${
                              activeSession?.id === session.id 
                                ? 'border-primary bg-primary/5' 
                                : 'border-border hover:border-primary/50 hover:bg-muted/50'
                            }`}
                            onClick={() => setActiveSession(session)}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Terminal className="h-4 w-4" />
                                <span className="font-medium text-sm">{session.name}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                {getSessionBadge(session.status)}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={async (e) => {
                                    e.stopPropagation()
                                    const result = await closeSession(session.id)
                                    toast({
                                      title: result.success ? 'Sessione chiusa' : 'Chiusura sessione fallita',
                                      description: result.success ? session.name : result.error || 'Errore sconosciuto',
                                      variant: result.success ? 'default' : 'destructive',
                                    })
                                  }}
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Commands: {session.command_count} | 
                              Last activity: {new Date(session.last_activity).toLocaleString('it-IT')}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <EnterpriseEmptyState
                        icon={<Terminal className="h-8 w-8 opacity-50" />}
                        title="Nessuna sessione terminale"
                        description="Crea una sessione per iniziare ad eseguire comandi."
                      />
                    )}
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="history" className="space-y-4">
                  <ScrollArea className="h-64">
                    {isLoading ? (
                      <EnterpriseSectionSkeleton rows={5} showHeader={false} rowClassName="h-14" />
                    ) : commandHistory.length > 0 ? (
                      <div className="space-y-2">
                        {commandHistory.map((cmd) => (
                          <div key={cmd.id} className="p-2 bg-muted/50 rounded">
                            <div className="flex items-center justify-between mb-1">
                              <code className="text-sm font-mono">{cmd.command}</code>
                              <div className="flex items-center gap-2">
                                {cmd.exit_code === 0 ? (
                                  <CheckCircle className="h-3 w-3 text-green-500" />
                                ) : (
                                  <AlertCircle className="h-3 w-3 text-red-500" />
                                )}
                                <span className="text-xs text-muted-foreground">
                                  {cmd.duration_ms}ms
                                </span>
                              </div>
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {new Date(cmd.executed_at).toLocaleString('it-IT')}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <EnterpriseEmptyState
                        icon={<Clock className="h-8 w-8 opacity-50" />}
                        title="Nessuno storico comandi"
                        description="Lo storico verra popolato dopo le prime esecuzioni."
                      />
                    )}
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Process Monitor */}
          <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-primary" />
                Process Monitor
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-72 sm:h-96">
                {isLoading ? (
                  <EnterpriseSectionSkeleton rows={7} showHeader={false} rowClassName="h-24" />
                ) : processes.length > 0 ? (
                  <div className="space-y-2">
                    {processes.map((process) => (
                      <div key={process.pid} className="p-3 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Zap className="h-4 w-4" />
                            <span className="font-medium text-sm">{process.name}</span>
                          </div>
                          {getProcessStatusBadge(process.status)}
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 text-xs">
                          <div>
                            <span className="text-muted-foreground">PID:</span>
                            <span className="ml-1 font-mono">{process.pid}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">User:</span>
                            <span className="ml-1">{process.user}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">CPU:</span>
                            <span className="ml-1 font-mono">{process.cpu_percent.toFixed(1)}%</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Memory:</span>
                            <span className="ml-1 font-mono">{process.memory_percent.toFixed(1)}%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EnterpriseEmptyState
                    icon={<Activity className="h-8 w-8 opacity-50" />}
                    title="Nessun processo disponibile"
                    description="Il monitor processi non ha restituito dati."
                  />
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Error Display */}
        {error && (
          <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/20">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                <AlertCircle className="h-4 w-4" />
                <span className="font-medium">Errore di connessione</span>
              </div>
              <p className="text-sm text-red-600 dark:text-red-400 mt-1">{error}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  )
}
